import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { dataFetch, getSession } from '../api';

const EcommerceContext = createContext();

export const EcommerceProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('em_cart');
    return saved ? JSON.parse(saved) : [];
  });

  const [wishlist, setWishlist] = useState(() => {
    const saved = localStorage.getItem('em_wishlist');
    return saved ? JSON.parse(saved) : [];
  });

  const [groupBuys, setGroupBuys] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Load group buys and notifications from NoCodeBackend
  const fetchData = useCallback(async () => {
    const user = getSession();
    if (!user || isFetching) return;

    setIsFetching(true);
    try {
      // Fetch Group Buys
      const gbRes = await dataFetch('read/group_buys_1741164676?sort=created_at&order=desc&limit=100');

      if (gbRes?.status === 'success' && Array.isArray(gbRes?.data)) {
        const processedGBs = gbRes.data.map(gb => ({
          id: gb.id,
          productId: gb.product_id,
          productName: gb.product_name,
          productImage: gb.product_image,
          goal: Number(gb.goal),
          current: Number(gb.current_count),
          deadline: gb.deadline,
          status: gb.status,
          owner_id: gb.owner_id,
          description: gb.description || ''
        }));
        setGroupBuys(processedGBs);
        checkAndHandleDeadlines(processedGBs);
      }

      // Fetch notifications
      const notifRes = await dataFetch(`read/notifications_1741164676?user_id=${user.id}&limit=50&sort=created_at&order=desc`);
      if (notifRes?.status === 'success' && Array.isArray(notifRes?.data)) {
        setNotifications(notifRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch e-commerce data', err);
    } finally {
      setIsFetching(false);
    }
  }, [isFetching]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const handler = () => setTimeout(fetchData, 1500);
    window.addEventListener('memorial_activity', handler);
    return () => window.removeEventListener('memorial_activity', handler);
  }, [fetchData]);

  useEffect(() => { localStorage.setItem('em_cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem('em_wishlist', JSON.stringify(wishlist)); }, [wishlist]);

  const checkAndHandleDeadlines = async (gbs) => {
    const now = new Date();
    for (const gb of gbs) {
      if (gb.status === 'active' && new Date(gb.deadline) < now) {
        const isSuccess = gb.current >= gb.goal;
        const newStatus = isSuccess ? 'completed' : 'failed';

        await dataFetch(`update/group_buys_1741164676/${gb.id}`, {
          method: 'PUT',
          body: JSON.stringify({ status: newStatus, updated_at: new Date().toISOString() })
        });

        const partRes = await dataFetch(`read/group_buy_participants_1741164676?group_buy_id=${gb.id}`);

        if (partRes?.status === 'success' && Array.isArray(partRes?.data)) {
          for (const participant of partRes.data) {
            await createNotification({
              userId: participant.user_id,
              title: isSuccess ? 'Group Buy Success!' : 'Group Buy Failed',
              message: isSuccess
                ? `The goal for ${gb.productName} was reached! Your order is being processed.`
                : `The group buy for ${gb.productName} did not reach its goal. A full refund has been initiated.`,
              type: isSuccess ? 'success' : 'failure',
              ownerId: participant.user_id
            });
          }
        }
      }
    }
  };

  const createNotification = async ({ userId, title, message, type, ownerId }) => {
    await dataFetch('create/notifications_1741164676', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        title,
        message,
        type,
        owner_id: ownerId,
        is_read: 0,
        created_at: new Date().toISOString(),
      })
    });
  };

  const markNotificationAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    await dataFetch(`update/notifications_1741164676/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_read: true, updated_at: new Date().toISOString() })
    });
  };

  const deleteNotification = async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    await dataFetch(`delete/notifications_1741164676/${id}`, { method: 'DELETE' });
  };

  const clearAllNotifications = async () => {
    const ids = notifications.map(n => n.id);
    setNotifications([]);
    await Promise.allSettled(
      ids.map(id => dataFetch(`delete/notifications_1741164676/${id}`, { method: 'DELETE' }))
    );
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const variantKey = product.selectedVariations
        ? product.selectedVariations.join('|')
        : '';
      const cartItemId = `${product.id}-${variantKey}`;

      const existing = prev.find(item => (item.cartItemId || item.id) === cartItemId);
      if (existing) {
        return prev.map(item =>
          (item.cartItemId || item.id) === cartItemId ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { ...product, cartItemId, quantity }];
    });
  };

  const startGroupBuy = async (product, goal, deadline, description = '') => {
    const user = getSession();
    const ownerId = user?.id || 'anonymous';
    const now = new Date().toISOString();

    const newGroupBuy = {
      product_id: product.id,
      product_name: product.name,
      product_image: product.image,
      goal: Number(goal),
      current_count: 1,
      deadline: new Date(deadline).toISOString(),
      status: 'active',
      owner_id: ownerId,
      description,
      created_at: now,
      updated_at: now,
    };

    const res = await dataFetch('create/group_buys_1741164676', {
      method: 'POST',
      body: JSON.stringify(newGroupBuy)
    });

    if (res?.status === 'success' && res?.data?.id) {
      await dataFetch('create/group_buy_participants_1741164676', {
        method: 'POST',
        body: JSON.stringify({
          group_buy_id: res.data.id,
          user_id: ownerId,
          user_name: user?.name || 'Anonymous',
          owner_id: ownerId,
          joined_at: now,
        })
      });
      fetchData();
      return res.data;
    }
  };

  const joinGroupBuy = async (groupBuyId) => {
    const user = getSession();
    const ownerId = user?.id || 'anonymous';
    const userName = user?.name || 'Anonymous';
    const now = new Date().toISOString();

    const gb = groupBuys.find(g => g.id === groupBuyId);
    if (!gb || gb.status !== 'active') return;

    const newCount = gb.current + 1;
    const isSuccess = newCount >= gb.goal;
    const newStatus = isSuccess ? 'completed' : 'active';

    await dataFetch(`update/group_buys_1741164676/${groupBuyId}`, {
      method: 'PUT',
      body: JSON.stringify({
        current_count: newCount,
        status: newStatus,
        updated_at: now,
      })
    });

    await dataFetch('create/group_buy_participants_1741164676', {
      method: 'POST',
      body: JSON.stringify({
        group_buy_id: groupBuyId,
        user_id: ownerId,
        user_name: userName,
        owner_id: ownerId,
        joined_at: now,
      })
    });

    if (isSuccess) {
      const partRes = await dataFetch(`read/group_buy_participants_1741164676?group_buy_id=${groupBuyId}`);
      if (partRes?.status === 'success' && Array.isArray(partRes?.data)) {
        for (const p of partRes.data) {
          createNotification({
            userId: p.user_id,
            title: 'Group Buy Success!',
            message: `The goal for ${gb.productName} has been reached!`,
            type: 'success',
            ownerId: p.user_id
          });
        }
      }
    }

    fetchData();
  };

  return (
    <EcommerceContext.Provider value={{
      cart, wishlist, groupBuys, notifications,
      addToCart,
      removeFromCart: (cartItemId) => setCart(prev => prev.filter(item => (item.cartItemId || item.id) !== cartItemId)),
      toggleWishlist: (p) => setWishlist(prev =>
        prev.find(i => i.id === p.id) ? prev.filter(i => i.id !== p.id) : [...prev, p]
      ),
      clearCart: () => setCart([]),
      startGroupBuy,
      joinGroupBuy,
      markNotificationAsRead,
      deleteNotification,
      clearAllNotifications,
    }}>
      {children}
    </EcommerceContext.Provider>
  );
};

export const useEcommerce = () => useContext(EcommerceContext);